import * as React from 'react'
import './Graph.css'

import * as _ from 'lodash'
import {Constraint, makeBox, tree, Box, setVars, boxContains, parseConstraintTemplate as kfParse, parseExpressionTemplate as kfEParse} from 'kiwifruit'
import {styleFor, propsFor, KiwifruitUser, KiwifruitSolver, KiwifruitVisualizer, AddConstraint, GetVariable} from 'kiwifruit-react'
import {NetworkKiwifruitUser} from 'golgi-kiwifruit'

import WithContextMenu, { MenuMaker } from './WithContextMenu'
import Steps from './Steps'
// import { callIdDefiningNodeId, graphTrunkNodeId, callIdUsingNodeId } from '../model'
// import * as actions from '../actions'
import { documentC, debugMode } from '../globals'
import StepProps from './StepProps'
import * as Model from '../types/document'
import { NetworkUser } from 'golgi'
import { Cursor, CursorV } from '../lib/Cursor'
import { Stack } from '../types/stack';
import { graphTrunkNodeId } from 'src/model/graph';
import * as actions from '../actions'
import NewInputDropSite from 'src/view/NewInputDropSite';
import TrunkCall from 'src/view/TrunkCall';
import Sizer, { Size } from 'src/view/Sizer';
import { nodeInfo, GraphNodeInfo } from 'src/model/nodeInfo';
import Call from 'src/view/Call';
import Node from 'src/view/Node'
import UnmountWhiner from 'src/view/UnmountWhiner';
import { clog } from 'src/util';


interface SubtreeArgs {
  nodeId: Model.NodeId,
  rootBox: Box,
  subtreeBox: Box,
  addConstraint: AddConstraint,
  graphId: string,
  getVariable: GetVariable,
}


const subtreeVMargin = 40
const cornerRadius = 10


class Graph extends React.Component<StepProps<Model.Graph>> {

  state = {
    expandedCallIds: {} as {[callId: string]: boolean},
    callMapActiveIndices: {} as {[callId: string]: number | string},
    nodeSizes: {} as {[nodeId: string]: Size},
  }

  render() {
    return <KiwifruitSolver><NetworkKiwifruitUser debugName="Graph" props={this.props} state={this.state} render={({ getVariable, addConstraint }) => {
      const {stepC: graphC, stepsC, blockC, stack} = this.props
      const {expandedCallIds} = this.state

      const graphCV = graphC.fetch()
      const graph = graphCV.value()

      const trunkNodeId = graphTrunkNodeId(graph)

      const id = graph.id
      const graphBox = makeBox(id + "-graph", addConstraint)
      addConstraint(
        ...tree.box({box: graphBox, left: 0, top: 0, shrink: true})
      )
      const rootBox = makeBox(id + '-root-self', addConstraint)
      
      const width = getVariable(graphBox.width)
      const height = getVariable(graphBox.height)

      return (
        <div className="Graph">
          { debugMode &&
            <div style={{fontSize: "70%"}}>
              {graphC.get().id}
            </div>
          }
          <WithContextMenu menuMaker={() => [{
            type: 'heading',
            heading: "GRAPH",
          }, {
            type: 'contents',
            contents: "Delete graph",
            onClick: () => actions.act(() => actions.deleteStepFromSteps(graphC))
          }]}>
            <div style={{width, height, position: "relative"}}>
              {this.renderSubtree({
                nodeId: trunkNodeId,
                rootBox: rootBox,
                subtreeBox: graphBox,
                addConstraint, graphId: id, getVariable
              })}
              <TrunkCall 
                nodeId={trunkNodeId} graphC={graphC} blockC={blockC} stack={stack}
                graphBox={graphBox} rootBox={rootBox}/>
              <KiwifruitVisualizer/>
            </div>
          </WithContextMenu>

          {_.map(expandedCallIds, (expanded, callId) => {
            if (expanded) {
              const callC = graphC.select('calls').select(callId) as Cursor<Model.DefinitionCall>
              const newStack: Stack = [...stack, {type: 'call', callP: callC.path()}]
              const callDefinitionId = callC.select('definition').get()
              return <div key={callId} className="Graph-expanded-call" data-call={callId} data-stack={btoa(JSON.stringify(stack))}>
                <Steps blockC={Cursor.selectPattern(documentC.select('definitions'), {id: callDefinitionId})} stack={newStack} />
              </div>
            } else {
              return undefined
            }
          })}
        </div>
      )
    }} /></KiwifruitSolver>
  }

  setCallExpanded = (callId: Model.CallId, expanded: boolean) => {
    const {expandedCallIds} = this.state
    this.setState({expandedCallIds: {...expandedCallIds, [callId]: expanded}})
  }

  renderSubtree(args: SubtreeArgs) {
    const { nodeId, addConstraint, rootBox, subtreeBox, graphId, getVariable } = args
    const { stepC: graphC, stepsC, blockC, stack } = this.props
    const { expandedCallIds, callMapActiveIndices, nodeSizes } = this.state
    
    const id = graphId + '-' + nodeId

    // TODO
    const definingCallId = (nodeInfo(blockC, nodeId) as GraphNodeInfo).definingCallId
    if (!definingCallId) {
      // This is an invisible reference node
      return <div></div>
    }
    const definingCallC = graphC.select2('calls', definingCallId)
    const definingCall = definingCallC.get()

    const brokenCall = definingCall.broken

    // Construct & constrain self & "main"
    const mainBox = makeBox(id + '-main', addConstraint)
    const aboveMainBox = makeBox(id + '-above-main', addConstraint)
    addConstraint(...
      tree.column({box: subtreeBox, shrink: true, spacing: brokenCall ? subtreeVMargin : 0},
        aboveMainBox,
        mainBox,
      )
    )

    // Construct & constrain root node
    const {width: nodeWidth = 0, height: nodeHeight = 0} = nodeSizes[nodeId] || {}
    addConstraint(...
      tree.box({box: mainBox},
        tree.box({box: rootBox, width: nodeWidth, height: nodeHeight})  // TODO: get rid of side-channel size
      )
    )

    let callRendering = undefined as React.ReactNode
    let inputsRendering = [] as Array<React.ReactNode>
    let inlineCallToEditC = undefined
    if (definingCall.callType === 'code' && definingCall.collapsed) {
      // This is a node with a collapsed call
      inlineCallToEditC = definingCallC as Cursor<Model.CodeCall>
    } else {
      // This is a node with children

      // Construct & constrain child subtrees and roots
      const inputsC = definingCallC.select('inputs')
      const inputCs = CursorV.elems(inputsC.fetch())
      const childSubtreeBoxes = inputCs.map((_input, i) => makeBox(id + "-subtree-" + i, addConstraint))
      const childRootBoxes = inputCs.map((_input, i) => makeBox(id + "-root-" + i, addConstraint))

      addConstraint(...
        tree.box({box: mainBox},
          tree.column({name: id + '-child-column', spacing: subtreeVMargin, align: 'right'},
            childSubtreeBoxes
          )
        )
      )

      let firstChildSubtreeBox = undefined as Box | undefined
      let firstChildRootBox = undefined as Box | undefined
      let firstChildConnectorBox = undefined as Box | undefined
      if (brokenCall) {
        firstChildSubtreeBox = makeBox(id + "-first-child-subtree", addConstraint)
        firstChildRootBox = makeBox(id + "-first-child-root", addConstraint)
        firstChildConnectorBox = makeBox(id + "-first-child-connector", addConstraint)

        addConstraint(...
          tree.box({box: aboveMainBox},
            firstChildSubtreeBox
          )
        )

        addConstraint(...
          tree.box({box: childSubtreeBoxes[0], height: 30, shrink: true},
            childRootBoxes[0])
        )

        addConstraint(
          ...boxContains(firstChildConnectorBox, {x: kfEParse`${childRootBoxes[0].right} + 20`, y: kfEParse`${childRootBoxes[0].vMid}`}, 2),
          ...boxContains(firstChildConnectorBox, {x: kfEParse`${childRootBoxes[0].right}`, y: kfEParse`${childRootBoxes[0].vMid}`}, 2),
          ...boxContains(firstChildConnectorBox, {x: kfEParse`${firstChildRootBox.hMid}`, y: kfEParse`${firstChildRootBox.bottom}`}, 2),
          kfParse`${firstChildRootBox.hMid} >= ${childRootBoxes[0].right} + ${2 * cornerRadius}`,
          Constraint.weak(kfParse`${firstChildRootBox.hMid} - ${childRootBoxes[0].right} == 0`)
        )
      }

      // Construct & constrain call
      const callBox = makeBox(id + '-call', addConstraint)
      addConstraint(...
        tree.box({box: mainBox},
          callBox
        )
      )

      callRendering = [
        <Call
          key={"Call-" + definingCallId}
          callC={definingCallC}
          graphC={graphC}
          blockC={blockC}
          stack={stack}
          callBox={callBox}
          inputBoxes={childRootBoxes}
          outputBox={rootBox}
          expanded={expandedCallIds[definingCallId]}
          setExpanded={(expanded) => actions.act(() => {
            this.setCallExpanded(definingCall.id, expanded)
          })}
          activeIndex={callMapActiveIndices[definingCallId] || 0}
        />,
        ...(brokenCall ? (() => {
          const horY = (getVariable(aboveMainBox.bottom) + getVariable(mainBox.top)) / 2

          const points = [
            [getVariable(firstChildRootBox!.hMid), getVariable(firstChildRootBox!.bottom)],
            [getVariable(firstChildRootBox!.hMid), horY],
            [getVariable(childRootBoxes[0].right), horY],
            [getVariable(childRootBoxes[0].right), getVariable(childRootBoxes[0].vMid)],
            [getVariable(childRootBoxes[0].right) + 20, getVariable(childRootBoxes[0].vMid)]
          ]

          const path = `
            M ${points[0][0]} ${points[0][1]}
            L ${points[1][0]} ${points[1][1] - cornerRadius}
            a ${cornerRadius} ${cornerRadius} 0 0 1 ${-cornerRadius} ${cornerRadius}
            L ${points[2][0] + cornerRadius} ${points[2][1]}
            a ${cornerRadius} ${cornerRadius} 0 0 0 ${-cornerRadius} ${cornerRadius}
            L ${points[3][0]} ${points[3][1] - cornerRadius}
            a ${cornerRadius} ${cornerRadius} 0 0 0 ${cornerRadius} ${cornerRadius}
            L ${points[4][0]} ${points[4][1]}
          `

          return [
            <svg key={"connector-" + definingCallId}
                 className="Graph-first-child-connector-svg"
                {...propsFor.svg(getVariable, firstChildConnectorBox!)}
                style={{...styleFor.svg(getVariable, firstChildConnectorBox!)}}>
              <path d={path} />
            </svg>
            ]
        })() : [])
      ]

      inputsRendering = _.flatten(inputCs.map((inputC, i) =>
        this.renderSubtree({
          nodeId: inputC.value().outside,
          rootBox: brokenCall && i == 0 ? firstChildRootBox! : childRootBoxes[i],
          subtreeBox: brokenCall && i == 0 ? firstChildSubtreeBox! : childSubtreeBoxes[i],
          addConstraint, graphId, getVariable
        })
      ))
    }

    let activeIndex
    const callUsingId = (nodeInfo(blockC, nodeId) as GraphNodeInfo).usingCallId
    const callUsingC = callUsingId && graphC.select('calls').select(callUsingId)
    const callUsing = callUsingC && callUsingC.get()
    const callDefiningId = (nodeInfo(blockC, nodeId) as GraphNodeInfo).definingCallId
    const callDefiningC = graphC.select('calls').select(callDefiningId)
    const callDefining = callDefiningC.get()
    if (callUsing && callUsing.callType === 'list-op') {
      activeIndex = callMapActiveIndices[callUsing.id] || 0
    } else if (callDefining.callType === 'list-op' && callDefining.listOpType === 'map') {
      activeIndex = callMapActiveIndices[callDefining.id] || 0
    }

    const nodeMenuMaker: MenuMaker =
      callUsingC
      ? () => [
          { type: 'heading', heading: "INPUT" },
          { type: 'contents',
            contents: "Delete input",
            onClick: () => actions.act(() => {
              actions.deleteInputFromCall(nodeId, callUsingC.get().id, graphC, blockC)
            })
          },
        ]
      : () => []

    const nodeRendering = (
      <WithContextMenu menuMaker={nodeMenuMaker}>
        <Node nodeId={nodeId}
              blockC={blockC}
              inlineCallToEditC={inlineCallToEditC}
              stack={stack}
              activeIndex={activeIndex}
              setActiveIndex={(i: number | string) => {
                console.log("setActiveIndex", i)
                if (callUsing && callUsing.callType === 'list-op') {
                  this.setState({callMapActiveIndices: {...callMapActiveIndices, [callUsing.id]: i}})
                } else if (callDefining.callType === 'list-op' && callDefining.listOpType === 'map') {
                  this.setState({callMapActiveIndices: {...callMapActiveIndices, [callDefiningId]: i}})
                }
              }}
              subtreeComponent={this}/>
      </WithContextMenu>
    )

    return [
      ...inputsRendering,
      <div key={"node-" + nodeId}>
        <div style={styleFor.div(getVariable, rootBox)}>
          <Sizer owner={this} stateName="nodeSizes" stateKey={nodeId}>
            {nodeRendering}
          </Sizer>
        </div>
        {callRendering}
      </div>
    ]
  }
  
  componentWillUnmount() {
    // console.log("Graph UNmount")
  }
}


export default Graph;



// WEBPACK FOOTER //
// ./src/view/Graph.tsx